import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useModal } from '../../../components/layout';
import { useProfileManagement } from '../../../components/auth/useProfileManagement';
import WebSourceForm from '../../../components/data-sources/web-sources/WebSourceForm';
import { API_ENDPOINTS } from '../../../config/api.config';
import { WebSourceFormData } from '../../../types';
import { transformFormDataToWebSource } from '../../../utils/webSourceUtils';
import { apiService } from '../../../services/apiService';
import ContentSkeleton from '../../../components/loading/ContentSkeleton';

const CreateWebSource: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const modalState = useModal();
    const [isLoading, setIsLoading] = useState(true);

    useProfileManagement(
        undefined,
        modalState.setError,
        modalState.setSuccess,
        modalState.setIsLoading,
        modalState.setShowProfileModal,
        modalState.setShowPasswordModal
    );

    // Brief loading state for consistency
    useEffect(() => {
        const timer = setTimeout(() => setIsLoading(false), 100);
        return () => clearTimeout(timer);
    }, []);

    const handleSubmit = async (formData: WebSourceFormData) => {
        modalState.setIsLoading(true);
        modalState.setError(null);

        try {
            const apiData = transformFormDataToWebSource(formData);
            await apiService.post(API_ENDPOINTS.DATA_SOURCE.WEB_SOURCES.CREATE, apiData);
            modalState.setSuccess('Web source created successfully');
            navigate('/data-sources/web');
        } catch (err) {
            console.error('Error creating web source:', err);
            modalState.setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            modalState.setIsLoading(false);
        }
    };

    const handleCancel = () => {
        navigate('/data-sources/web');
    };

    if (!user) return null;

    return (
        <div className="page-container">
            <div className="content-header">
                <h5 className="mb-4 fw-semibold">New Web Source</h5>
                {isLoading ? (
                    <ContentSkeleton />
                ) : (
                    <WebSourceForm
                        onSubmit={handleSubmit}
                        onCancel={handleCancel}
                        error={modalState.error}
                        isLoading={modalState.isLoading}
                        isEdit={false}
                    />
                )}
            </div>
        </div>
    );
};

export default CreateWebSource;